import apiClient, { API_BASE_URL } from '../utils/apiClient';

const BASE = `${API_BASE_URL}/api/support`;

export interface TicketReply {
    id: string;
    ticket_id: string;
    author_id: string;
    author_name: string;
    message: string;
    created_at: string;
}

export interface SupportTicket {
    id: string;
    user_id: string;
    user_name: string;
    user_email: string | null;
    category: string;
    subject: string;
    description: string | null;
    image_urls: string[];
    status: 'open' | 'in_progress' | 'resolved';
    created_at: string;
    updated_at: string | null;
    replies: TicketReply[];
}

export interface TicketListResponse {
    items: SupportTicket[];
    total: number;
    page: number;
    page_size: number;
}

export interface UserTicketStat {
    user_id: string;
    user_name: string;
    user_email: string | null;
    open: number;
    in_progress: number;
    resolved: number;
    total: number;
}

export interface TicketStats {
    total: number;
    open: number;
    in_progress: number;
    resolved: number;
    by_user: UserTicketStat[];
    total_users: number;
    page: number;
    page_size: number;
}

const supportService = {
    async submitTicket(
        category: string,
        subject: string,
        description: string,
        images: File[]
    ): Promise<SupportTicket> {
        const fd = new FormData();
        fd.append('category', category);
        fd.append('subject', subject);
        if (description) fd.append('description', description);
        images.forEach((img) => fd.append('images', img));

        const res = await apiClient(`${BASE}/tickets`, {
            method: 'POST',
            body: fd,
        });
        if (!res.ok) throw new Error('Failed to submit ticket');
        return res.json();
    },

    async getMyTickets(page = 1, pageSize = 10): Promise<TicketListResponse> {
        const res = await apiClient(`${BASE}/tickets/mine?page=${page}&page_size=${pageSize}`);
        if (!res.ok) throw new Error('Failed to load tickets');
        return res.json();
    },

    async getTicketById(id: string): Promise<SupportTicket> {
        const res = await apiClient(`${BASE}/tickets/${id}`);
        if (!res.ok) throw new Error('Failed to load ticket');
        return res.json();
    },

    async getAllTickets(
        page = 1,
        pageSize = 20,
        filters: { status?: string; user_id?: string; search?: string } = {}
    ): Promise<TicketListResponse> {
        const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
        if (filters.status) params.set('status', filters.status);
        if (filters.user_id) params.set('user_id', filters.user_id);
        if (filters.search) params.set('search', filters.search);
        const res = await apiClient(`${BASE}/tickets?${params.toString()}`);
        if (!res.ok) throw new Error('Failed to load all tickets');
        return res.json();
    },

    async replyToTicket(ticketId: string, message: string): Promise<TicketReply> {
        const fd = new FormData();
        fd.append('message', message);
        const res = await apiClient(`${BASE}/tickets/${ticketId}/reply`, { method: 'POST', body: fd });
        if (!res.ok) throw new Error('Failed to post reply');
        return res.json();
    },

    async userReplyToTicket(ticketId: string, message: string): Promise<TicketReply> {
        const fd = new FormData();
        fd.append('message', message);
        const res = await apiClient(`${BASE}/tickets/${ticketId}/user-reply`, { method: 'POST', body: fd });
        if (!res.ok) throw new Error('Failed to send reply');
        return res.json();
    },

    async acknowledgeTicket(ticketId: string): Promise<TicketReply> {
        const res = await apiClient(`${BASE}/tickets/${ticketId}/acknowledge`, { method: 'POST' });
        if (!res.ok) throw new Error('Failed to acknowledge ticket');
        return res.json();
    },

    async getStats(params: {
        search?: string;
        status?: string;
        page?: number;
        pageSize?: number;
    } = {}): Promise<TicketStats> {
        const p = new URLSearchParams();
        if (params.search) p.set('search', params.search);
        if (params.status && params.status !== 'total') p.set('status', params.status);
        if (params.page) p.set('page', String(params.page));
        if (params.pageSize) p.set('page_size', String(params.pageSize));
        const qs = p.toString();
        const res = await apiClient(`${BASE}/tickets/stats${qs ? `?${qs}` : ''}`);
        if (!res.ok) throw new Error('Failed to load stats');
        return res.json();
    },

    async updateTicketStatus(ticketId: string, status: string): Promise<SupportTicket> {
        const fd = new FormData();
        fd.append('status', status);
        const res = await apiClient(`${BASE}/tickets/${ticketId}/status`, { method: 'PATCH', body: fd });
        if (!res.ok) throw new Error('Failed to update status');
        return res.json();
    },
};

export default supportService;
